//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { deserializeMediaItem } from '@apis/media-library/mediaLibraryApiUtils';
import {
  CreateYouTubePlaylistRequest,
  GetYouTubeAudienceAnalyticsRequest,
  GetYouTubeAudienceMetricsResult,
  GetYouTubeEngagementAnalyticsRequest,
  GetYouTubeEngagementMetricsResult,
  GetYouTubePlaylistsRequest,
  GetYouTubeVideosCsvRequest,
  GetYouTubeVideosRequest,
  GetYouTubeVideosResult,
  YouTubePaginatedPlaylists,
  YouTubePlaylist,
  YouTubeUser,
  YouTubeVideoCategory,
} from '@apis/youtube/youtubeApiModel';
import { isDefined } from '@utils/filterPredicates';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const YOUTUBE_DEFAULT_ENDPOINT = '/api/v1';

const youtubeGatewayFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'youtube_gateway',
    `${YOUTUBE_DEFAULT_ENDPOINT}${endpoint}`,
    init,
  );
};

/**
 * Gets the client's default connected YouTube account
 *
 * GET /api/v1/user
 */
export const getYouTubeUser = async (
  init?: RequestInit,
): Promise<YouTubeUser> => {
  const res = await youtubeGatewayFetch(`/user`, init);

  return (await res.json<{ data: YouTubeUser }>()).data;
};

/**
 * Gets playlists for the connected youtube account
 *
 * GET /api/v1/playlist
 */
export const getYouTubePlaylists = async (
  req: GetYouTubePlaylistsRequest,
  init?: RequestInit,
): Promise<YouTubePaginatedPlaylists> => {
  const params = makeParams(req);
  const res = await youtubeGatewayFetch(`/playlist?${params}`, init);

  return (await res.json<{ data: YouTubePaginatedPlaylists }>()).data;
};

/**
 * Gets video categories for the connected youtube account
 *
 * GET /api/v1/video/categories
 */
export const getYouTubeVideoCategories = async (
  init?: RequestInit,
): Promise<YouTubeVideoCategory[]> => {
  const res = await youtubeGatewayFetch(`/video/categories`, init);

  return (await res.json<{ data: YouTubeVideoCategory[] }>()).data;
};

/**
 * Creates playlist for the connected youtube account
 *
 * POST /api/v1/playlist
 */
export const createYouTubePlaylist = async (
  body: CreateYouTubePlaylistRequest,
  init?: RequestInit,
): Promise<YouTubePlaylist> => {
  const res = await youtubeGatewayFetch(`/playlist`, {
    ...(isDefined(init) ? init : {}),
    body: JSON.stringify(body),
    method: 'POST',
  });

  return (await res.json<{ data: YouTubePlaylist }>()).data;
};

/**
 * Get audience analytics for the connected youtube account
 *
 * GET /api/v1/analytics/audience
 */
export const getYouTubeAudienceAnalytics = async (
  req: GetYouTubeAudienceAnalyticsRequest,
  init?: RequestInit,
): Promise<GetYouTubeAudienceMetricsResult> => {
  const params = makeParams(req);
  const res = await youtubeGatewayFetch(`/analytics/audience?${params}`, init);

  return (
    await res.json<{ data: GetYouTubeAudienceMetricsResult }>((res) => ({
      data: {
        ...res.data,
        earliestDate: isDefined(res.data.earliestDate)
          ? new Date(res.data.earliestDate)
          : undefined,
      },
    }))
  ).data;
};

/**
 * Get engagment analytics for the connected youtube account
 *
 * GET /api/v1/analytics/engagment
 */
export const getYouTubeEngagementAnalytics = async (
  req: GetYouTubeEngagementAnalyticsRequest,
  init?: RequestInit,
): Promise<GetYouTubeEngagementMetricsResult> => {
  const params = makeParams(req);
  const res = await youtubeGatewayFetch(
    `/analytics/engagement?${params}`,
    init,
  );

  return (
    await res.json<{ data: GetYouTubeEngagementMetricsResult }>((res) => ({
      data: {
        ...res.data,
        dailyMetrics: res.data.dailyMetrics.map((dailyMetric) => ({
          ...dailyMetric,
          date: new Date(dailyMetric.date),
        })),
      },
    }))
  ).data;
};

/**
 * Get videos posted on the connected youtube account
 *
 * GET /api/v1/analytics/videos
 */
export const getYouTubeVideos = async (
  req: GetYouTubeVideosRequest,
  init?: RequestInit,
): Promise<GetYouTubeVideosResult> => {
  const params = makeParams(req);
  const res = await youtubeGatewayFetch(`/analytics/videos?${params}`, init);

  return (
    await res.json<{ data: GetYouTubeVideosResult }>((res) => ({
      data: { ...res.data, videos: res.data.videos.map(deserializeMediaItem) },
    }))
  ).data;
};

/**
 * Get CSV of videos posted on the connected youtube account
 *
 * GET /api/v1/analytics/videos/csv
 */
export const getYouTubeVideosCsv = async (
  req: GetYouTubeVideosCsvRequest,
): Promise<string> => {
  const params = makeParams(req);
  const res = await youtubeGatewayFetch(`/analytics/videos/csv?${params}`);

  return res.text();
};
